import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip, Button, Popover, Divider, Chip, CircularProgress } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { toPng } from 'html-to-image';

import { Category } from '../../types/Category';
import { helpContent } from '../../constants/helpContent';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import FormGroup from '@mui/material/FormGroup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { useHelpContent } from '../../utils/HelpUtils';
import { invertSubcaseSelection } from '../../utils/CategoryUtils';
import { InvertSelectionButton } from '../category/CategoryContent';

/** PNG export uses fixed light colors so output matches on-screen light mode in any theme. */
const PNG_EXPORT_LIGHT = {
  paper: '#ffffff',
  textPrimary: 'rgba(0, 0, 0, 0.87)',
  textSecondary: 'rgba(0, 0, 0, 0.6)',
  textDisabled: 'rgba(0, 0, 0, 0.38)',
  primary: '#1976d2',
  edgeStroke: alpha('#123a52', 0.35),
};

const highlightColors = [
  '#ffe066', // yellow
  '#ffadad', // red
  '#b5ead7', // green
  '#a0c4ff', // blue
  '#ffd6a5', // orange
  '#caffbf', // light green
  '#9bf6ff', // cyan
  '#bdb2ff', // purple
];

type HorizontalNodePos = { x: number; y: number };

function buildCategoryIndex(root: Category): Map<string, Category> {
  const m = new Map<string, Category>();
  const walk = (c: Category) => {
    m.set(c.id, c);
    (c.children ?? []).forEach(walk);
  };
  walk(root);
  return m;
}

/** Selected subcases that have at least one content row in this content category. */
function buildCoveragePresent(root: Category, selectedSubcases: string[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const walk = (cat: Category) => {
    if (cat.contentCategory) {
      const present = new Set<string>();
      if (selectedSubcases.length > 0 && cat.contents) {
        for (const co of cat.contents) {
          if (co.subcase && selectedSubcases.includes(co.subcase)) present.add(co.subcase);
        }
      }
      map.set(cat.id, present);
    }
    (cat.children ?? []).forEach(walk);
  };
  walk(root);
  return map;
}

/** More coverage → darker fill (content categories only when shading is on). */
function coverageFillRatio(presentCount: number, totalSelected: number): number {
  if (totalSelected <= 0) return 0;
  return Math.max(0, Math.min(1, presentCount / totalSelected));
}

function coverageBackground(ratio: number): string {
  const c0 = [255, 255, 255];
  /** Full coverage — darker blue so 100% reads clearly vs partial fills. */
  const c1 = [22, 92, 140];
  const r = Math.round(c0[0] + (c1[0] - c0[0]) * ratio);
  const g = Math.round(c0[1] + (c1[1] - c0[1]) * ratio);
  const b = Math.round(c0[2] + (c1[2] - c0[2]) * ratio);
  return `rgb(${r},${g},${b})`;
}

/** Readable title on coverage gradient (theme-agnostic — dark-mode `text.primary` is light and fails on pale fills). */
function textColorForCoverageBackground(bg: string): string {
  const m = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(bg.trim());
  if (!m) return 'rgba(0,0,0,0.87)';
  const r = parseInt(m[1], 10);
  const g = parseInt(m[2], 10);
  const b = parseInt(m[3], 10);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 138 ? 'rgba(0,0,0,0.87)' : '#fff';
}

function textShadowForCoverageTitle(bg: string): string {
  const m = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(bg.trim());
  if (!m) return 'none';
  const r = parseInt(m[1], 10);
  const g = parseInt(m[2], 10);
  const b = parseInt(m[3], 10);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 138 ? '0 0 1px rgba(255,255,255,0.55)' : '0 0 2px rgba(0,0,0,0.35)';
}

/** Stronger node outline — visible on white fills and dark coverage shading. */
function categoryNodeFrameSx(theme: Theme, forPngExport = false) {
  if (forPngExport) {
    return {
      border: `2px solid ${alpha('#123a52', 0.78)}`,
      boxShadow: `0 1px 4px ${alpha('#000', 0.16)}, 0 0 0 1px ${alpha('#123a52', 0.12)}`,
    };
  }
  const borderColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.48)
      : alpha('#123a52', 0.78);
  return {
    border: `2px solid ${borderColor}`,
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 1px 4px ${alpha(theme.palette.common.black, 0.55)}`
        : `0 1px 4px ${alpha(theme.palette.common.black, 0.16)}, 0 0 0 1px ${alpha('#123a52', 0.12)}`,
  };
}

const SUBCASE_LIST_MAX_SHOWN = 9;

const HorizontalSubTreeGraph: React.FC<{
  rootCategory: Category;
  isRTL: boolean;
  onLayout?: (info: { canvasW: number; rootX: number; rootY: number }) => void;
  scale?: number;
  selectedSubcases: string[];
  coverageShading: boolean;
  showSubcaseList: boolean;
  /** When true, render with light-mode colors for PNG capture (parent theme may be dark). */
  forPngExport?: boolean;
}> = ({
  rootCategory,
  isRTL,
  onLayout,
  scale = 1,
  selectedSubcases,
  coverageShading,
  showSubcaseList,
  forPngExport = false,
}) => {
  const theme = useTheme();
  const [expandedSubcaseListById, setExpandedSubcaseListById] = React.useState<Record<string, boolean>>({});
  const selectedSubcasesKey = selectedSubcases.join('\u0001');

  React.useEffect(() => {
    setExpandedSubcaseListById({});
  }, [rootCategory.id, showSubcaseList, selectedSubcasesKey]);

  const NODE_W = 240;
  const NODE_MIN_W = 200;
  const H_GAP = 40;
  const V_GAP = 110;
  const MIN_NODE_H = 52;
  // p.y = vertical center of the category Paper (fixed). Subcase list hangs below and does not move the Paper.
  // Padding: room above first row’s paper top (paper top = p.y - paperH/2).
  const PAD_X = NODE_W / 2 + 24;
  const PAD_Y = MIN_NODE_H / 2 + 28;

  const { positions, edges, canvas, categoryById, coveragePresent, paperHeightById, listBelowById } =
    useMemo(() => {
    const catMap = buildCategoryIndex(rootCategory);
    const cov = buildCoveragePresent(rootCategory, selectedSubcases);

    /** Category title box only (subcase list is laid out below the Paper). */
    const TITLE_PAPER_H = 44 + 12;

    const paperHeightByIdLocal: Record<string, number> = {};
    /** Height from bottom of Paper to bottom of subcase list (mt + lines); 0 if no list. */
    const listBelowByIdLocal: Record<string, number> = {};

    const computeHeight = (cat: Category): void => {
      if (showSubcaseList && cat.contentCategory) {
        const present = cov.get(cat.id) ?? new Set();
        const ordered =
          selectedSubcases.length > 0 ? selectedSubcases.filter(s => present.has(s)) : [];
        let lineCount = 1;
        if (ordered.length > 0) {
          if (expandedSubcaseListById[cat.id]) {
            lineCount =
              ordered.length +
              (ordered.length > SUBCASE_LIST_MAX_SHOWN ? 1 : 0);
          } else {
            lineCount =
              Math.min(ordered.length, SUBCASE_LIST_MAX_SHOWN) +
              (ordered.length > SUBCASE_LIST_MAX_SHOWN ? 1 : 0);
          }
        }
        const listBelowPaper = 6 + lineCount * 15;
        paperHeightByIdLocal[cat.id] = Math.max(MIN_NODE_H, TITLE_PAPER_H);
        listBelowByIdLocal[cat.id] = listBelowPaper;
      } else {
        const h = Math.max(MIN_NODE_H, TITLE_PAPER_H);
        paperHeightByIdLocal[cat.id] = h;
        listBelowByIdLocal[cat.id] = 0;
      }
      (cat.children ?? []).forEach(computeHeight);
    };
    computeHeight(rootCategory);

    const pos: Record<string, HorizontalNodePos> = {};
    const edgeList: Array<{ from: string; to: string }> = [];

    const subtreeWidth = (n: Category): number => {
      const kids = n.children ?? [];
      if (!kids.length) return NODE_W;
      const widths = kids.map(subtreeWidth);
      const totalKids = widths.reduce((a, b) => a + b, 0) + H_GAP * (widths.length - 1);
      return Math.max(NODE_W, totalKids);
    };

    const place = (n: Category, left: number, depth: number) => {
      const kids = n.children ?? [];
      const w = subtreeWidth(n);
      const cx = left + w / 2;
      const cy = PAD_Y + depth * V_GAP;
      pos[n.id] = { x: cx, y: cy };

      if (!kids.length) return;

      let curLeft = left + (w - (kids.map(subtreeWidth).reduce((a, b) => a + b, 0) + H_GAP * (kids.length - 1))) / 2;
      for (const ch of kids) {
        const cw = subtreeWidth(ch);
        edgeList.push({ from: n.id, to: ch.id });
        place(ch, curLeft, depth + 1);
        curLeft += cw + H_GAP;
      }
    };

    const totalW = subtreeWidth(rootCategory) + PAD_X * 2;
    place(rootCategory, PAD_X, 0);

    let maxBottom = 0;
    for (const [id, p] of Object.entries(pos)) {
      const ph = paperHeightByIdLocal[id] ?? MIN_NODE_H;
      const below = listBelowByIdLocal[id] ?? 0;
      maxBottom = Math.max(maxBottom, p.y + ph / 2 + below);
    }
    const totalH = maxBottom + PAD_Y;

    return {
      positions: pos,
      edges: edgeList,
      canvas: { w: totalW, h: totalH },
      categoryById: catMap,
      coveragePresent: cov,
      paperHeightById: paperHeightByIdLocal,
      listBelowById: listBelowByIdLocal,
    };
  }, [rootCategory, selectedSubcases, showSubcaseList, expandedSubcaseListById]);

  React.useEffect(() => {
    if (!onLayout) return;
    const rootPos = positions[rootCategory.id];
    if (!rootPos) return;
    onLayout({ canvasW: canvas.w, rootX: rootPos.x, rootY: rootPos.y });
  }, [onLayout, positions, rootCategory.id, canvas.w]);

  const totalSelected = selectedSubcases.length;

  return (
    <Box
      sx={{
        position: 'relative',
        width: canvas.w,
        height: canvas.h,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <svg
        width={canvas.w}
        height={canvas.h}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {edges.map((e) => {
          const a = positions[e.from];
          const b = positions[e.to];
          if (!a || !b) return null;
          const phA = paperHeightById[e.from] ?? MIN_NODE_H;
          const phB = paperHeightById[e.to] ?? MIN_NODE_H;
          const listA = listBelowById[e.from] ?? 0;
          const x1 = a.x;
          const y1 = a.y + phA / 2 + listA;
          const x2 = b.x;
          const y2 = b.y - phB / 2;
          const midY = (y1 + y2) / 2;
          const d = `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
          return (
            <path
              key={`${e.from}-${e.to}`}
              d={d}
              fill="none"
              stroke={
                forPngExport
                  ? PNG_EXPORT_LIGHT.edgeStroke
                  : theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.32)
                    : alpha('#123a52', 0.35)
              }
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}
      </svg>

      {Object.entries(positions).map(([id, p]) => {
        const cat = categoryById.get(id);
        const name = cat?.name ?? id;
        const isRoot = id === rootCategory.id;
        const present = cat?.contentCategory ? coveragePresent.get(id) ?? new Set<string>() : null;
        const ratio =
          coverageShading && cat?.contentCategory && totalSelected > 0 && present
            ? coverageFillRatio(present.size, totalSelected)
            : 0;
        const bg =
          coverageShading && cat?.contentCategory && totalSelected > 0
            ? coverageBackground(ratio)
            : forPngExport
              ? PNG_EXPORT_LIGHT.paper
              : theme.palette.background.paper;
        const orderedSubs =
          present && selectedSubcases.length > 0 ? selectedSubcases.filter(s => present.has(s)) : [];
        const listExpanded = !!expandedSubcaseListById[id];
        const shown = listExpanded ? orderedSubs : orderedSubs.slice(0, SUBCASE_LIST_MAX_SHOWN);
        const more = listExpanded ? 0 : orderedSubs.length - shown.length;
        const covTitle =
          cat?.contentCategory && totalSelected > 0 && present
            ? `Subcase coverage: ${present.size} / ${totalSelected} selected`
            : cat?.contentCategory
              ? 'Content category'
              : '';

        const ph = paperHeightById[id] ?? MIN_NODE_H;
        const useCoverageContrast =
          coverageShading && cat?.contentCategory && totalSelected > 0;
        const coverageTitleColor = useCoverageContrast ? textColorForCoverageBackground(bg) : null;

        return (
          <Tooltip key={id} title={covTitle} disableHoverListener={!covTitle}>
            <Box
              sx={{
                position: 'absolute',
                left: p.x,
                top: p.y - ph / 2,
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                width: NODE_W,
                minWidth: NODE_MIN_W,
                maxWidth: 360,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 1.75,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: bg,
                  width: '100%',
                  minHeight: ph,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  ...categoryNodeFrameSx(theme, forPngExport),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isRoot ? 800 : 700,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    color: coverageTitleColor ?? (forPngExport ? PNG_EXPORT_LIGHT.textPrimary : 'text.primary'),
                    textShadow: coverageTitleColor ? textShadowForCoverageTitle(bg) : 'none',
                  }}
                >
                  {name}
                </Typography>
              </Paper>
              {showSubcaseList && cat?.contentCategory && (
                <Box
                  sx={{
                    mt: 0.75,
                    width: '100%',
                    textAlign: isRTL ? 'right' : 'left',
                  }}
                >
                  {selectedSubcases.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: forPngExport ? PNG_EXPORT_LIGHT.textSecondary : 'text.secondary',
                        display: 'block',
                      }}
                    >
                      (select subcases in the dialog for coverage list)
                    </Typography>
                  ) : orderedSubs.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: forPngExport ? PNG_EXPORT_LIGHT.textDisabled : 'text.disabled',
                        display: 'block',
                      }}
                    >
                      — none of the selected subcases
                    </Typography>
                  ) : (
                    <>
                      {shown.map(s => (
                        <Typography
                          key={s}
                          variant="caption"
                          sx={{
                            display: 'block',
                            lineHeight: 1.35,
                            color: forPngExport ? PNG_EXPORT_LIGHT.textSecondary : 'text.secondary',
                          }}
                        >
                          • {s}
                        </Typography>
                      ))}
                      {more > 0 && (
                        <Typography
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSubcaseListById((prev) => ({ ...prev, [id]: true }));
                          }}
                          sx={{
                            display: 'block',
                            mt: 0.25,
                            color: forPngExport ? PNG_EXPORT_LIGHT.primary : 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontStyle: 'italic',
                            border: 0,
                            background: 'none',
                            padding: 0,
                            width: '100%',
                            textAlign: 'inherit',
                            font: 'inherit',
                          }}
                        >
                          +{more} more…
                        </Typography>
                      )}
                      {listExpanded && orderedSubs.length > SUBCASE_LIST_MAX_SHOWN && (
                        <Typography
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSubcaseListById((prev) => {
                              const next = { ...prev };
                              delete next[id];
                              return next;
                            });
                          }}
                          sx={{
                            display: 'block',
                            mt: 0.25,
                            color: 'text.secondary',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            border: 0,
                            background: 'none',
                            padding: 0,
                            width: '100%',
                            textAlign: 'inherit',
                            font: 'inherit',
                          }}
                        >
                          Show less
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

function highlightText(
  text: string,
  searchWords: string[],
  caseSensitive: boolean,
  regex: RegExp,
  colorMap: { [key: string]: string },
  selectedMatch?: { matchIdx: number; wordIdx: number },
  wholeWordOnly?: boolean
) {
  if (!searchWords.length || !text) return text;
  const normalizedText = text.replace(/\n/g, ' ');
  let lastIndex = 0;
  let matchIdx = 0;
  const parts = [];
  
  if (wholeWordOnly) {
    // Custom whole word highlighting
    searchWords.forEach((searchWord, wordIdx) => {
      const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
      let match;
      
      while ((match = searchRegex.exec(normalizedText)) !== null) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        
        // Check if it's a whole word by examining characters before and after
        const beforeChar = matchStart > 0 ? normalizedText[matchStart - 1] : '';
        const afterChar = matchEnd < normalizedText.length ? normalizedText[matchEnd] : '';
        
        // Check if characters before and after are not word characters
        const isWordChar = (char: string) => /[a-zA-Z0-9_\u0590-\u05FF]/.test(char);
        
        if (!isWordChar(beforeChar) && !isWordChar(afterChar)) {
          if (matchStart > lastIndex) {
            parts.push(text.slice(lastIndex, matchStart));
          }
          
          const colorKey = caseSensitive ? searchWord : searchWord.toLowerCase();
          const color = colorMap[colorKey] || '#ffe066';
          const isSelected = selectedMatch && selectedMatch.matchIdx === matchIdx && selectedMatch.wordIdx === wordIdx;
          
          parts.push(
            <span
              key={`${matchStart}-${wordIdx}`}
              style={{
                backgroundColor: color,
                border: isSelected ? '2px solid #ff9800' : undefined,
                borderRadius: isSelected ? 3 : undefined,
                boxShadow: isSelected ? '0 0 0 2px #ffe066' : undefined,
                padding: isSelected ? '0 2px' : undefined,
                position: 'relative',
                zIndex: isSelected ? 1 : 0,
              }}
            >
              {match[0]}
            </span>
          );
          
          lastIndex = matchEnd;
          matchIdx++;
        }
      }
    });
  } else {
    // Regular regex highlighting - need to check which word matched
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(normalizedText)) !== null) {
      if (!match[0]) {
        regex.lastIndex++;
        continue;
      }

      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const matchedText = match[0];
      
      // Find which word matched by checking against each search word
      let wordIdx = -1;
      for (let i = 0; i < searchWords.length; i++) {
        const searchWord = searchWords[i];
        const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
        if (searchRegex.test(matchedText)) {
          wordIdx = i;
          break;
        }
      }
      
      const colorKey = caseSensitive ? searchWords[wordIdx] : searchWords[wordIdx]?.toLowerCase();
      const color = colorMap[colorKey] || '#ffe066';
      const isSelected = selectedMatch && selectedMatch.matchIdx === matchIdx && selectedMatch.wordIdx === wordIdx;
      parts.push(
        <span
          key={match.index}
          style={{
            backgroundColor: color,
            border: isSelected ? '2px solid #ff9800' : undefined,
            borderRadius: isSelected ? 3 : undefined,
            boxShadow: isSelected ? '0 0 0 2px #ffe066' : undefined,
            padding: isSelected ? '0 2px' : undefined,
            position: 'relative',
            zIndex: isSelected ? 1 : 0,
          }}
        >
          {matchedText}
        </span>
      );
      lastIndex = match.index + matchedText.length;
      matchIdx++;
    }
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

function parseSearchTerms(input: string): [string[], boolean] {
  if (!input.trim()) return [[], false];

  // Check for quoted phrases first
  const quotedPhrases: string[] = [];
  let remainingInput = input;
  
  // Extract quoted phrases (both English and Hebrew quotes)
  const quoteRegex = /["״]([^"״]+)["״]/g;
  let match;
  while ((match = quoteRegex.exec(input)) !== null) {
    quotedPhrases.push(match[1]);
    // Remove the quoted phrase from remaining input
    remainingInput = remainingInput.replace(match[0], '');
  }
  
  // Split remaining input by whitespace and filter out empty terms
  const remainingTerms = remainingInput.split(/\s+/).filter(term => term.length > 0);
  
  // Combine quoted phrases and remaining terms
  const allTerms = [...quotedPhrases, ...remainingTerms];
  
  const hasRegex = allTerms.some(term => term.includes('*') || term.includes('?') || term.includes('+') || term.includes('[') || term.includes(']') || term.includes('(') || term.includes(')') || term.includes('\\') || term.includes('^') || term.includes('$') || term.includes('|') || term.includes('{') || term.includes('}') || term.includes('.'));
  
  if (hasRegex) {
    try {
      const regex = new RegExp(input, 'g');
      return [[input], true];
    } catch {
      return [allTerms, false];
    }
  }
  
  return [allTerms, false];
}

interface SubcaseGroupRow {
  name: string;
  subcases: string[];
}

interface SubTreeViewProps {
  rootCategory: Category;
  allCategories: Category[];
  subcases: string[];
  /** Document subcase names for the compare-mode pickers; defaults to union with `subcases` if omitted. */
  availableSubcases?: string[];
  /** Subcase groups (same model as the subtree overlay filter). Used in compare-mode popovers. */
  subcaseGroups?: SubcaseGroupRow[];
  subcaseGroupsLoading?: boolean;
  includeDescriptions: boolean;
  level?: number;
  sourceTexts?: { [key: string]: string[] }; // Map of content ID to array of source texts
  /** When both are set, layout mode is controlled by the parent (e.g. overlay toolbar). */
  layoutMode?: 'vertical' | 'horizontal';
  onLayoutModeChange?: (mode: 'vertical' | 'horizontal') => void;
}

function flattenTree(
  category: Category,
  level: number,
  includeDescriptions: boolean,
  subcases: string[],
  searchWords: string[],
  caseSensitive: boolean,
  regex: RegExp,
  colorMap: { [key: string]: string },
  searchFields: { category: boolean; description: boolean; subcase: boolean; content: boolean }
): Array<{
  type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
  content: string;
  level: number;
  id: string;
}> {
  const items: Array<{
    type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
    content: string;
    level: number;
    id: string;
  }> = [];

  // Add category name if it should be searched
  if (searchFields.category && category.name && category.name.trim()) {
    items.push({
      type: category.contentCategory ? 'contentCategory' : 'category',
      content: category.name,
      level,
      id: `category-${category.id}`
    });
  }

  // Add description if it should be searched and exists
  if (includeDescriptions && searchFields.description && category.description && category.description.trim()) {
    items.push({
      type: 'description',
      content: category.description,
      level: level + 1,
      id: `description-${category.id}`
    });
  }

  // Add contents if they match subcases
  if (subcases.length > 0 && category.contents) {
    category.contents
      .filter(content => subcases.includes(content.subcase))
      .forEach(content => {
        if (content.subcase && searchFields.subcase && content.subcase.trim()) {
          items.push({
            type: 'subcase',
            content: content.subcase,
            level: level + 1,
            id: `subcase-${content.id}`
          });
        }
        if (content.text && searchFields.content && content.text.trim()) {
          items.push({
            type: 'content',
            content: content.text,
            level: level + 1,
            id: `content-${content.id}`
          });
        }
      });
  }

  // Recursively add children
  if (category.children) {
    category.children.forEach(child => {
      items.push(...flattenTree(
        child,
        level + 1,
        includeDescriptions,
        subcases,
        searchWords,
        caseSensitive,
        regex,
        colorMap,
        searchFields
      ));
    });
  }

  return items;
}

/** One search hit for keyboard navigation (which pane + row + match indices). */
type SearchMatchPane = 0 | 1 | 2;
type VerticalComparePaneCount = 2 | 3;

function comparePaneTitle(pane: SearchMatchPane, paneCount: VerticalComparePaneCount): string {
  if (pane === 0) return 'Left';
  if (paneCount === 2) return 'Right';
  return pane === 1 ? 'Middle' : 'Right';
}

type ComparePaneAccent = 'error' | 'info' | 'secondary';

function comparePaneAccent(pane: SearchMatchPane, paneCount: VerticalComparePaneCount): ComparePaneAccent {
  if (pane === 0) return 'info';
  if (paneCount === 3 && pane === 1) return 'error';
  return 'secondary';
}

function comparePaneLabelColor(accent: ComparePaneAccent): string {
  if (accent === 'info') return 'info.dark';
  if (accent === 'error') return 'error.dark';
  return `${accent}.main`;
}

function compareColumnShellSx(
  accent: ComparePaneAccent,
  searchActive: boolean
): { column: object; header: object; labelColor: string; buttonColor: ComparePaneAccent } {
  return {
    column: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 1,
      overflow: 'hidden',
      transition: 'border-color 160ms ease, box-shadow 160ms ease',
      border: searchActive ? '2px solid' : '1px solid',
      borderColor: searchActive
        ? `${accent}.main`
        : (t: Theme) => alpha(t.palette[accent].main, 0.35),
      boxShadow: searchActive
        ? (t: Theme) => `0 0 0 3px ${alpha(t.palette[accent].main, 0.22)}`
        : undefined,
    },
    header: {
      px: 1,
      py: 0.75,
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
      bgcolor: (t: Theme) => alpha(t.palette[accent].main, searchActive ? 0.12 : 0.06),
    },
    labelColor: comparePaneLabelColor(accent),
    buttonColor: accent,
  };
}

function compareCategoryRowStyles(
  theme: Theme,
  accent: ComparePaneAccent,
  isContentCategory: boolean
): { iconColor: string; textColor: string } {
  const accentColor = theme.palette[accent].main;
  const accentTextColor =
    accent === 'info'
      ? theme.palette.info.dark
      : accent === 'error'
        ? theme.palette.error.dark
        : accentColor;

  if (isContentCategory) {
    return {
      iconColor: accentColor,
      textColor: accentTextColor,
    };
  }

  return {
    iconColor: accentColor,
    textColor: theme.palette.text.primary,
  };
}

type SearchMatchEntry = {
  pane: SearchMatchPane;
  itemIdx: number;
  matchIdx: number;
  wordIdx: number;
};

type TreeRowItem = {
  type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
  content: string;
  level: number;
  id: string;
};

function categoryRowId(item: TreeRowItem | undefined | null): string | null {
  if (!item) return null;
  if (item.type !== 'category' && item.type !== 'contentCategory') return null;
  return item.id.startsWith('category-') ? item.id.slice('category-'.length) : item.id;
}

/** Category id for the row at `rowIndex` (nearest category header at or above). */
function categoryIdForRowIndex(itemList: TreeRowItem[], rowIndex: number): string | null {
  if (itemList.length === 0 || rowIndex < 0) return null;
  const clamped = Math.min(rowIndex, itemList.length - 1);
  for (let i = clamped; i >= 0; i--) {
    const id = categoryRowId(itemList[i]);
    if (id) return id;
  }
  return null;
}

/** First virtuoso row index per category id (for scroll alignment). */
function buildCategoryRowIndexMap(itemList: TreeRowItem[]): Map<string, number> {
  const map = new Map<string, number>();
  itemList.forEach((item, index) => {
    const id = categoryRowId(item);
    if (id && !map.has(id)) map.set(id, index);
  });
  return map;
}

type ContentCategoryNavTarget = { categoryId: string; rowIndex: number; label: string };

function findAdjacentContentCategory(
  itemList: TreeRowItem[],
  referenceCategoryId: string | null,
  direction: 1 | -1
): ContentCategoryNavTarget | null {
  const contentRows: ContentCategoryNavTarget[] = [];
  itemList.forEach((item, index) => {
    if (item.type === 'contentCategory') {
      const id = categoryRowId(item);
      if (id) {
        contentRows.push({ categoryId: id, rowIndex: index, label: item.content.trim() });
      }
    }
  });
  if (contentRows.length === 0) return null;

  if (!referenceCategoryId) {
    return direction === 1 ? contentRows[0] : contentRows[contentRows.length - 1];
  }

  const refRowIndex = itemList.findIndex((row) => categoryRowId(row) === referenceCategoryId);
  const refContentIdx = contentRows.findIndex((row) => row.categoryId === referenceCategoryId);

  if (refContentIdx >= 0) {
    const nextIdx = refContentIdx + direction;
    if (nextIdx < 0 || nextIdx >= contentRows.length) return null;
    return contentRows[nextIdx];
  }

  if (refRowIndex >= 0) {
    if (direction === 1) {
      return contentRows.find((row) => row.rowIndex > refRowIndex) ?? null;
    }
    return [...contentRows].reverse().find((row) => row.rowIndex < refRowIndex) ?? null;
  }

  return direction === 1 ? contentRows[0] : contentRows[contentRows.length - 1];
}

function collectSearchMatches(
  itemList: TreeRowItem[],
  pane: SearchMatchPane,
  searchWords: string[],
  wholeWordOnly: boolean,
  regex: RegExp,
  caseSensitive: boolean
): SearchMatchEntry[] {
  const matches: SearchMatchEntry[] = [];

  itemList.forEach((item, itemIdx) => {
    if (!item.content) return;

    const normalizedText = item.content.replace(/\n/g, ' ');

    if (wholeWordOnly) {
      searchWords.forEach((searchWord, wordIdx) => {
        const searchRegex = new RegExp(
          searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
          caseSensitive ? 'g' : 'gi'
        );
        let match;
        let matchIdx = 0;

        while ((match = searchRegex.exec(normalizedText)) !== null) {
          const matchStart = match.index;
          const matchEnd = matchStart + match[0].length;

          const beforeChar = matchStart > 0 ? normalizedText[matchStart - 1] : '';
          const afterChar = matchEnd < normalizedText.length ? normalizedText[matchEnd] : '';

          const isWordChar = (char: string) => /[a-zA-Z0-9_\u0590-\u05FF]/.test(char);

          if (!isWordChar(beforeChar) && !isWordChar(afterChar)) {
            matches.push({ pane, itemIdx, matchIdx, wordIdx });
            matchIdx++;
          }
        }
      });
    } else {
      const rx = new RegExp(regex.source, regex.flags);
      let match;
      let matchIdx = 0;

      while ((match = rx.exec(normalizedText)) !== null) {
        if (!match[0]) {
          rx.lastIndex++;
          continue;
        }

        let wordIdx = -1;
        for (let i = 1; i < match.length; i++) {
          if (match[i] !== undefined) {
            wordIdx = i - 1;
            break;
          }
        }

        matches.push({ pane, itemIdx, matchIdx, wordIdx });
        matchIdx++;
      }
    }
  });

  return matches;
}

const MAX_HISTORY = 5;

const SubTreeView: React.FC<SubTreeViewProps> = ({
  rootCategory,
  allCategories,
  subcases,
  availableSubcases: availableSubcasesProp,
  subcaseGroups: subcaseGroupsProp,
  subcaseGroupsLoading = false,
  includeDescriptions,
  level = 0,
  sourceTexts,
  layoutMode: layoutModeProp,
  onLayoutModeChange,
}) => {
  const theme = useTheme();
  const isRTL = useMemo(() => /[\u0590-\u05FF]/.test(rootCategory.name), [rootCategory.name]);
  const compareVirtuosoScroller = useMemo(
    () =>
      React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(function CompareVirtuosoScroller(
        props,
        ref
      ) {
        return <div {...props} ref={ref} dir={isRTL ? 'rtl' : 'ltr'} />;
      }),
    [isRTL]
  );
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [itemHeights, setItemHeights] = React.useState<{ [key: string]: number }>({});
  const contentRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  const virtuosoRef = React.useRef<VirtuosoHandle>(null);
  const virtuosoRefMiddle = React.useRef<VirtuosoHandle>(null);
  const virtuosoRefRight = React.useRef<VirtuosoHandle>(null);
  const compareSyncLockRef = React.useRef(false);
  const compareLastCategoryByPaneRef = React.useRef<Partial<Record<SearchMatchPane, string>>>({});
  const compareLeftVisibleStartRef = React.useRef(0);
  const compareRangeDebounceRef = React.useRef<Partial<Record<SearchMatchPane, number>>>({});
  const [compareCategorySync, setCompareCategorySync] = React.useState(() => {
    try {
      return localStorage.getItem('subtree_compare_category_sync') !== '0';
    } catch {
      return true;
    }
  });
  const [syncedCategoryLabel, setSyncedCategoryLabel] = React.useState<string | null>(null);

  const [search, setSearch] = React.useState('');
  const [caseSensitive, setCaseSensitive] = React.useState(false);
  const [wholeWordOnly, setWholeWordOnly] = React.useState(false);
  const [searchHistory, setSearchHistory] = React.useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('subtree_search_history');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [searchBoxAnchor, setSearchBoxAnchor] = React.useState<null | HTMLElement>(null);

  // Search fields state
  const [searchFields, setSearchFields] = React.useState({
    category: true,
    description: true,
    subcase: true,
    content: true,
  });

  // State for expanded source texts
  const [expandedSources, setExpandedSources] = React.useState<{ [key: string]: boolean }>({});
  const [showSourceTexts, setShowSourceTexts] = React.useState(false);
  const [layoutModeInternal, setLayoutModeInternal] = React.useState<'vertical' | 'horizontal'>(() => {
    try {
      const saved = localStorage.getItem('subtree_layout_mode');
      return saved === 'horizontal' ? 'horizontal' : 'vertical';
    } catch {
      return 'vertical';
    }
  });
  const layoutModeControlled =
    layoutModeProp !== undefined && onLayoutModeChange !== undefined;
  const layoutMode = layoutModeControlled ? layoutModeProp : layoutModeInternal;

  const [verticalCompareSplit, setVerticalCompareSplit] = React.useState(() => {
    try {
      return (
        localStorage.getItem('subtree_layout_mode') !== 'horizontal' &&
        localStorage.getItem('subtree_vertical_compare_split') === '1'
      );
    } catch {
      return false;
    }
  });

  const applyLayoutMode = React.useCallback(
    (v: 'vertical' | 'horizontal') => {
      if (layoutModeControlled) {
        onLayoutModeChange(v);
      } else {
        setLayoutModeInternal(v);
      }
      if (v === 'horizontal') {
        setVerticalCompareSplit(false);
      }
      try {
        localStorage.setItem('subtree_layout_mode', v);
      } catch {
        /* ignore */
      }
    },
    [layoutModeControlled, onLayoutModeChange],
  );
  const [verticalLeftSubcases, setVerticalLeftSubcases] = React.useState<string[]>([]);
  const [verticalMiddleSubcases, setVerticalMiddleSubcases] = React.useState<string[]>([]);
  const [verticalRightSubcases, setVerticalRightSubcases] = React.useState<string[]>([]);
  const [verticalLeftGroupNames, setVerticalLeftGroupNames] = React.useState<string[]>([]);
  const [verticalMiddleGroupNames, setVerticalMiddleGroupNames] = React.useState<string[]>([]);
  const [verticalRightGroupNames, setVerticalRightGroupNames] = React.useState<string[]>([]);
  const verticalCompareLeftBaselineRef = React.useRef<string[] | null>(null);
  const verticalCompareMiddleBaselineRef = React.useRef<string[] | null>(null);
  const verticalCompareRightBaselineRef = React.useRef<string[] | null>(null);
  const [verticalComparePaneCount, setVerticalComparePaneCount] = React.useState<VerticalComparePaneCount>(() => {
    try {
      return localStorage.getItem('subtree_vertical_compare_pane_count') === '3' ? 3 : 2;
    } catch {
      return 2;
    }
  });
  const [compareFilterOpen, setCompareFilterOpen] = React.useState<{
    which: 'left' | 'middle' | 'right';
    anchor: HTMLElement;
  } | null>(null);
  const [horizontalZoom, setHorizontalZoom] = React.useState<number>(() => {
    try {
      const saved = localStorage.getItem('subtree_horizontal_zoom');
      const z = saved ? Number(saved) : 1;
      return Number.isFinite(z) && z > 0 ? z : 1;
    } catch {
      return 1;
    }
  });
  const horizontalScrollRef = React.useRef<HTMLDivElement | null>(null);
  const horizontalLayoutRef = React.useRef<{ canvasW: number; rootX: number; rootY: number } | null>(null);
  const horizontalExportRef = React.useRef<HTMLDivElement | null>(null);
  const [exportingHorizontalPng, setExportingHorizontalPng] = React.useState(false);
  const horizontalDragRef = React.useRef<{
    isDragging: boolean;
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
  }>({
    isDragging: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });

  const [horizontalCoverageShading, setHorizontalCoverageShading] = React.useState(() => {
    try {
      return localStorage.getItem('subtree_horizontal_coverage') === '1';
    } catch {
      return false;
    }
  });
  const [horizontalShowSubcaseList, setHorizontalShowSubcaseList] = React.useState(() => {
    try {
      return localStorage.getItem('subtree_horizontal_subcase_list') === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_horizontal_coverage', horizontalCoverageShading ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [horizontalCoverageShading]);

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_horizontal_subcase_list', horizontalShowSubcaseList ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [horizontalShowSubcaseList]);

  const compareActive = layoutMode === 'vertical' && verticalCompareSplit;
  const treeSubcasesLeft = compareActive ? verticalLeftSubcases : subcases;
  const subcaseGroups = subcaseGroupsProp ?? [];

  const pickerSubcases = useMemo(() => {
    const s = new Set<string>();
    for (const x of availableSubcasesProp ?? []) {
      if (x) s.add(x);
    }
    for (const x of subcases) {
      if (x) s.add(x);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [availableSubcasesProp, subcases]);

  const docSubcasesForFilter = useMemo(() => {
    const raw = (availableSubcasesProp ?? []).filter(Boolean);
    if (raw.length > 0) {
      return Array.from(new Set(raw)).sort((a, b) => a.localeCompare(b));
    }
    return pickerSubcases;
  }, [availableSubcasesProp, pickerSubcases]);

  const handleCompareGroupSelect = React.useCallback(
    (side: 'left' | 'middle' | 'right', groupName: string) => {
      const group = subcaseGroups.find((g) => g.name === groupName);
      if (!group) return;

      const docSubcases = docSubcasesForFilter;
      const allDocSubcasesSelected = (selected: string[]) =>
        docSubcases.length > 0 &&
        selected.length === docSubcases.length &&
        docSubcases.every((s) => selected.includes(s));

      const unionForNames = (names: string[]) => {
        const acc = new Set<string>();
        names.forEach((n) => {
          const g = subcaseGroups.find((x) => x.name === n);
          g?.subcases.forEach((s) => acc.add(s));
        });
        return acc;
      };

      const applyForPane = (
        setGroupNames: React.Dispatch<React.SetStateAction<string[]>>,
        setSubcases: React.Dispatch<React.SetStateAction<string[]>>,
        baselineRef: React.MutableRefObject<string[] | null>
      ) => {
        setGroupNames((prevNames) => {
          const turningOff = prevNames.includes(groupName);
          const nextNames = turningOff ? prevNames.filter((n) => n !== groupName) : [...prevNames, groupName];

          setSubcases((prevSub) => {
            if (!turningOff) {
              if (prevNames.length === 0) {
                baselineRef.current = [...prevSub];
              }
              if (allDocSubcasesSelected(prevSub) && prevNames.length === 0) {
                return [...group.subcases];
              }
              const merged = [...prevSub];
              for (const s of group.subcases) {
                if (merged.indexOf(s) === -1) merged.push(s);
              }
              return merged;
            }
            const cover = unionForNames(nextNames);
            const filtered = prevSub.filter((s) => !group.subcases.includes(s) || cover.has(s));
            if (nextNames.length === 0) {
              if (baselineRef.current !== null) {
                const restored = [...baselineRef.current];
                baselineRef.current = null;
                return restored;
              }
              return filtered.length === 0 ? [...docSubcases] : filtered;
            }
            return filtered;
          });

          return nextNames;
        });
      };

      if (side === 'left') {
        applyForPane(setVerticalLeftGroupNames, setVerticalLeftSubcases, verticalCompareLeftBaselineRef);
      } else if (side === 'middle') {
        applyForPane(setVerticalMiddleGroupNames, setVerticalMiddleSubcases, verticalCompareMiddleBaselineRef);
      } else {
        applyForPane(setVerticalRightGroupNames, setVerticalRightSubcases, verticalCompareRightBaselineRef);
      }
    },
    [subcaseGroups, docSubcasesForFilter]
  );

  React.useEffect(() => {
    if (verticalCompareSplit) return;
    verticalCompareLeftBaselineRef.current = null;
    verticalCompareMiddleBaselineRef.current = null;
    verticalCompareRightBaselineRef.current = null;
    setVerticalLeftGroupNames([]);
    setVerticalMiddleGroupNames([]);
    setVerticalRightGroupNames([]);
    setCompareFilterOpen(null);
  }, [verticalCompareSplit]);

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_vertical_compare_pane_count', verticalComparePaneCount === 3 ? '3' : '2');
    } catch {
      /* ignore */
    }
  }, [verticalComparePaneCount]);

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_vertical_compare_split', verticalCompareSplit ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [verticalCompareSplit]);

  React.useEffect(() => {
    if (layoutMode === 'horizontal' && verticalCompareSplit) {
      setVerticalCompareSplit(false);
    }
  }, [layoutMode, verticalCompareSplit]);

  React.useEffect(() => {
    if (layoutMode !== 'vertical' || !verticalCompareSplit) return;
    setVerticalLeftSubcases((prev) => (prev.length > 0 ? prev : [...subcases]));
    setVerticalRightSubcases((prev) => (prev.length > 0 ? prev : [...subcases]));
    if (verticalComparePaneCount === 3) {
      setVerticalMiddleSubcases((prev) => (prev.length > 0 ? prev : [...subcases]));
    }
  }, [layoutMode, verticalCompareSplit, verticalComparePaneCount, subcases]);

  const { getTooltipTextForState, getToolbarTooltipTextForState } = useHelpContent();

  const makeSafeFilename = (name: string) =>
    (name || 'subtree')
      .replace(/[<>:"/\\|?*]/g, '-') // Windows filename invalid characters
      .replace(/\s+/g, '-') // spaces -> hyphens
      .replace(/-+/g, '-') // collapse
      .replace(/^-|-$/g, ''); // trim hyphens

  // Save history to localStorage
  React.useEffect(() => {
    localStorage.setItem('subtree_search_history', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // Print functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = generatePrintContent();
    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    const rtlDir = isRTL ? 'dir="rtl"' : '';
    const rtlBodyStyle = isRTL ? 'direction: rtl; text-align: right;' : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html ${rtlDir}>
        <head>
          <title>Subtree View - ${rootCategory.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; ${rtlBodyStyle} }
            .category { margin: 10px 0; }
            .content { margin: 5px 0; }
            .description { margin: 5px 0; font-style: italic; color: #666; }
            .level-0 { font-weight: bold; font-size: 18px; }
            .level-1 { font-weight: bold; font-size: 16px; }
            .level-2 { font-weight: bold; font-size: 14px; }
            .level-3 { font-weight: bold; font-size: 12px; }
            .level-4 { font-weight: bold; font-size: 12px; }
            .level-5 { font-weight: bold; font-size: 12px; }
            .content-category { color: #87CEEB; }
            .regular-category { color: #1976d2; }
            .content-text { margin: 5px 0; }
            .search-highlight { background-color: #ffe066; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <h1>Subtree View: ${rootCategory.name}</h1>
          <p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>
          ${search ? `<p><strong>Search:</strong> "${search}"</p>` : ''}
          <div class="tree-content">${printContent}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleExportDocx = async () => {
    const printItems = flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      treeSubcasesLeft,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );

    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    
    const children = [
      new Paragraph({
        text: `Subtree View: ${rootCategory.name}`,
        heading: HeadingLevel.HEADING_1,
        alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
      new Paragraph({
        text: `Generated on: ${new Date().toLocaleString()}`,
        alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
    ];

    if (search) {
      children.push(
        new Paragraph({
          text: `Search: "${search}"`,
          alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
        })
      );
    }

    children.push(new Paragraph({ text: '' })); // Empty line

    printItems
      .filter(item => item.content && item.content.trim() !== '')
      .forEach(item => {
        let text = '';
        let icon = '';

        switch (item.type) {
          case 'category':
          case 'contentCategory':
            icon = item.type === 'contentCategory' ? '📄' : '📁';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
          case 'subcase':
            icon = '📝';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
          case 'content':
            text = item.content;
            break;
          case 'description':
            icon = '💬';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
        }

        if (text) {
          // Calculate indentation based on level
          const indent = item.level * 720; // 720 twips = 0.5 inches
          
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: text,
                  size: item.level === 0 ? 32 : item.level === 1 ? 28 : 24,
                  bold: item.type === 'category' || item.type === 'contentCategory',
                  italics: item.type === 'description',
                }),
              ],
              alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
              indent: {
                left: isRTL ? 0 : indent,
                right: isRTL ? indent : 0,
              },
            })
          );
        }
      });

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Create a safe filename that preserves Hebrew and other Unicode characters
    const safeName = makeSafeFilename(rootCategory.name);
    
    link.download = `subtree-view-${safeName}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportHorizontalPng = async () => {
    const node = horizontalExportRef.current;
    if (!node) return;

    try {
      setExportingHorizontalPng(true);
      const safeName = makeSafeFilename(rootCategory.name);

      // Capture the full horizontal graph container (not the scroll viewport).
      // Briefly switch to light export colors, then paint before html-to-image runs.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      const dataUrl = await toPng(node, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: Math.max(2, Math.round((window.devicePixelRatio || 1) * 2)),
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `subtree-horizontal-${safeName || 'export'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Failed to export horizontal subtree PNG:', e);
    } finally {
      setExportingHorizontalPng(false);
    }
  };

  const generatePrintContent = () => {
    const printItems = flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      treeSubcasesLeft,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );

    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    const rtlStyle = isRTL ? 'direction: rtl; text-align: right;' : '';
    const rtlPadding = isRTL ? 'padding-right' : 'padding-left';

    return printItems
      .filter(item => item.content && item.content.trim() !== '')
      .map(item => {
        const levelClass = `level-${Math.min(item.level, 5)}`;
        const paddingValue = item.level * 20;
        
        switch (item.type) {
          case 'category':
          case 'contentCategory': {
            const categoryClass = item.type === 'contentCategory' ? 'content-category' : 'regular-category';
            const icon = item.type === 'contentCategory' ? '📄' : '📁';
            return `<div class="category ${levelClass} ${categoryClass}" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">${icon} ${item.content}</div>`;
          }
                  case 'subcase': {
          return `<div class="subcase-text" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">📝 ${item.content}</div>`;
        }
        case 'content': {
          return `<div class="content-text" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">${item.content}</div>`;
        }
          case 'description': {
            return `<div class="description" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">💬 ${item.content}</div>`;
          }
          default:
            return '';
        }
      })
      .filter(item => item.trim() !== '')
      .join('');
  };

  // Add to history when search is submitted (on Enter or blur)
  const addToHistory = (value: string) => {
    if (!value.trim()) return;
    setSearchHistory(prev => {
      const filtered = prev.filter(item => item !== value);
      return [value, ...filtered].slice(0, MAX_HISTORY);
    });
  };

  // Handle search box events
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };
  const handleSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      addToHistory(search);
    }
  };
  const handleSearchBlur = () => {
    addToHistory(search);
  };
  const handleSearchFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setSearchBoxAnchor(e.currentTarget);
  };
  const handleHistoryClick = (item: string) => {
    setSearch(item);
    setSearchBoxAnchor(null);
  };

  // Parse search terms and create regex
  const [searchWords, isRegex] = useMemo(() => parseSearchTerms(search), [search]);
  const regex = useMemo(() => {
    if (isRegex) {
      try {
        return new RegExp(searchWords[0], caseSensitive ? 'g' : 'gi');
      } catch {
        return new RegExp('', 'g');
      }
    }
    
    if (!searchWords.length) return new RegExp('', 'g');
    
    if (wholeWordOnly) {
      // For whole word search, we'll use a custom approach
              const patterns = searchWords.map(word => {
          const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Use word boundaries that work with Unicode
          const pattern = `\\b${escaped}\\b`;
          return pattern;
        });
        
        const pattern = patterns.join('|');
      return new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    } else {
      // Regular search without whole word
      const patterns = searchWords.map(word => {
        const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return escaped;
      });
      
      const pattern = patterns.join('|');
      return new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    }
  }, [searchWords, isRegex, caseSensitive, wholeWordOnly]);

  // Create color map for highlighting
  const colorMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    searchWords.forEach((word, index) => {
      const key = caseSensitive ? word : word.toLowerCase();
      map[key] = highlightColors[index % highlightColors.length];
    });
    return map;
  }, [searchWords, caseSensitive]);

  // Flatten tree for display (left / single pane)
  const items = useMemo(() => {
    return flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      treeSubcasesLeft,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );
  }, [
    rootCategory,
    includeDescriptions,
    treeSubcasesLeft,
    searchWords,
    caseSensitive,
    regex,
    colorMap,
    searchFields,
  ]);

  const itemsMiddle = useMemo(() => {
    if (!compareActive || verticalComparePaneCount !== 3) return [];
    return flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      verticalMiddleSubcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );
  }, [
    compareActive,
    verticalComparePaneCount,
    rootCategory,
    includeDescriptions,
    verticalMiddleSubcases,
    searchWords,
    caseSensitive,
    regex,
    colorMap,
    searchFields,
  ]);

  const itemsRight = useMemo(() => {
    if (!compareActive) return [];
    return flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      verticalRightSubcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );
  }, [
    compareActive,
    rootCategory,
    includeDescriptions,
    verticalRightSubcases,
    searchWords,
    caseSensitive,
    regex,
    colorMap,
    searchFields,
  ]);

  const compareCategoryIndexMaps = useMemo(() => {
    const maps: Partial<Record<SearchMatchPane, Map<string, number>>> = {
      0: buildCategoryRowIndexMap(items as TreeRowItem[]),
    };
    if (verticalComparePaneCount === 3) {
      maps[1] = buildCategoryRowIndexMap(itemsMiddle as TreeRowItem[]);
      maps[2] = buildCategoryRowIndexMap(itemsRight as TreeRowItem[]);
    } else {
      maps[1] = buildCategoryRowIndexMap(itemsRight as TreeRowItem[]);
    }
    return maps;
  }, [items, itemsMiddle, itemsRight, verticalComparePaneCount]);

  const applyComparePaneCategorySync = React.useCallback(
    (sourcePane: SearchMatchPane, startIndex: number) => {
      if (!compareCategorySync || compareSyncLockRef.current) {
        return;
      }

      const sourceItems: TreeRowItem[] =
        sourcePane === 0
          ? (items as TreeRowItem[])
          : sourcePane === 1 && verticalComparePaneCount === 3
            ? (itemsMiddle as TreeRowItem[])
            : (itemsRight as TreeRowItem[]);
      if (startIndex < 0 || startIndex >= sourceItems.length) {
        return;
      }
      const categoryId = categoryIdForRowIndex(sourceItems, startIndex);
      if (!categoryId) {
        return;
      }

      const prevCategory = compareLastCategoryByPaneRef.current[sourcePane] ?? null;
      if (categoryId === prevCategory) {
        return;
      }

      compareLastCategoryByPaneRef.current[sourcePane] = categoryId;

      const categoryItem = sourceItems.find((row) => categoryRowId(row) === categoryId);
      const nextLabel = categoryItem?.content?.trim() || null;
      setSyncedCategoryLabel((prev) => (prev === nextLabel ? prev : nextLabel));

      const targets: { pane: SearchMatchPane; ref: React.RefObject<VirtuosoHandle | null> }[] =
        verticalComparePaneCount === 3
          ? [
              { pane: 0, ref: virtuosoRef },
              { pane: 1, ref: virtuosoRefMiddle },
              { pane: 2, ref: virtuosoRefRight },
            ]
          : [
              { pane: 0, ref: virtuosoRef },
              { pane: 1, ref: virtuosoRefRight },
            ];

      compareSyncLockRef.current = true;
      for (const target of targets) {
        if (target.pane === sourcePane) continue;
        const rowIndex = compareCategoryIndexMaps[target.pane]?.get(categoryId);
        if (rowIndex === undefined) continue;
        compareLastCategoryByPaneRef.current[target.pane] = categoryId;
        target.ref.current?.scrollToIndex({
          index: rowIndex,
          align: 'start',
          behavior: 'auto',
        });
      }
      window.setTimeout(() => {
        compareSyncLockRef.current = false;
      }, 150);
    },
    [
      compareCategorySync,
      items,
      itemsMiddle,
      itemsRight,
      compareCategoryIndexMaps,
      verticalComparePaneCount,
    ]
  );

  const scrollAllComparePanesToCategory = React.useCallback(
    (categoryId: string, label: string) => {
      const targets: { pane: SearchMatchPane; ref: React.RefObject<VirtuosoHandle | null> }[] =
        verticalComparePaneCount === 3
          ? [
              { pane: 0, ref: virtuosoRef },
              { pane: 1, ref: virtuosoRefMiddle },
              { pane: 2, ref: virtuosoRefRight },
            ]
          : [
              { pane: 0, ref: virtuosoRef },
              { pane: 1, ref: virtuosoRefRight },
            ];

      compareSyncLockRef.current = true;
      for (const target of targets) {
        const rowIndex = compareCategoryIndexMaps[target.pane]?.get(categoryId);
        if (rowIndex === undefined) continue;
        compareLastCategoryByPaneRef.current[target.pane] = categoryId;
        target.ref.current?.scrollToIndex({
          index: rowIndex,
          align: 'start',
          behavior: 'auto',
        });
      }
      setSyncedCategoryLabel((prev) => (prev === label ? prev : label || null));
      window.setTimeout(() => {
        compareSyncLockRef.current = false;
      }, 150);
    },
    [compareCategoryIndexMaps, verticalComparePaneCount]
  );

  const scrollCompareToCategory = React.useCallback(
    (categoryId: string, label: string) => {
      if (!compareCategorySync) return;
      scrollAllComparePanesToCategory(categoryId, label);
    },
    [compareCategorySync, scrollAllComparePanesToCategory]
  );

  const syncCompareFromLeftPane = React.useCallback(() => {
    if (!compareActive) return;
    const itemList = items as TreeRowItem[];
    if (itemList.length === 0) return;
    const startIndex = Math.min(
      Math.max(compareLeftVisibleStartRef.current, 0),
      itemList.length - 1
    );
    const categoryId = categoryIdForRowIndex(itemList, startIndex);
    if (!categoryId) return;
    const categoryItem = itemList.find((row) => categoryRowId(row) === categoryId);
    scrollAllComparePanesToCategory(categoryId, categoryItem?.content?.trim() || '');
  }, [compareActive, items, scrollAllComparePanesToCategory]);

  const jumpCompareContentCategory = React.useCallback(
    (direction: 1 | -1) => {
      const referenceId = compareLastCategoryByPaneRef.current[0] ?? null;
      const target = findAdjacentContentCategory(items as TreeRowItem[], referenceId, direction);
      if (!target) return;
      scrollCompareToCategory(target.categoryId, target.label);
    },
    [items, scrollCompareToCategory]
  );

  const compareContentCategoryNav = useMemo(() => {
    if (!compareActive || !compareCategorySync) {
      return { hasAny: false, canPrev: false, canNext: false };
    }
    const list = items as TreeRowItem[];
    const referenceId = compareLastCategoryByPaneRef.current[0] ?? null;
    return {
      hasAny: list.some((item) => item.type === 'contentCategory'),
      canPrev: findAdjacentContentCategory(list, referenceId, -1) !== null,
      canNext: findAdjacentContentCategory(list, referenceId, 1) !== null,
    };
  }, [compareActive, compareCategorySync, items, syncedCategoryLabel]);

  const handleComparePaneRangeChanged = React.useCallback(
    (sourcePane: SearchMatchPane, startIndex: number) => {
      if (sourcePane === 0) {
        compareLeftVisibleStartRef.current = startIndex;
      }
      if (!compareCategorySync || compareSyncLockRef.current || sourcePane !== 0) {
        return;
      }
      const existing = compareRangeDebounceRef.current[sourcePane];
      if (existing != null) {
        window.clearTimeout(existing);
      }
      compareRangeDebounceRef.current[sourcePane] = window.setTimeout(() => {
        compareRangeDebounceRef.current[sourcePane] = undefined;
        applyComparePaneCategorySync(sourcePane, startIndex);
      }, 120);
    },
    [compareCategorySync, applyComparePaneCategorySync]
  );

  React.useEffect(
    () => () => {
      Object.values(compareRangeDebounceRef.current).forEach((timer) => {
        if (timer != null) window.clearTimeout(timer);
      });
    },
    []
  );

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_compare_category_sync', compareCategorySync ? '1' : '0');
    } catch {
      // ignore
    }
  }, [compareCategorySync]);

  React.useEffect(() => {
    if (!compareActive) {
      compareLastCategoryByPaneRef.current = {};
      compareLeftVisibleStartRef.current = 0;
      setSyncedCategoryLabel(null);
      return;
    }
    compareLastCategoryByPaneRef.current = {};
    setSyncedCategoryLabel(null);
  }, [
    compareActive,
    verticalComparePaneCount,
    rootCategory.id,
    treeSubcasesLeft,
    verticalMiddleSubcases,
    verticalRightSubcases,
  ]);

  React.useEffect(() => {
    if (!compareActive || !compareCategorySync) return;
    const t = window.setTimeout(() => syncCompareFromLeftPane(), 80);
    return () => window.clearTimeout(t);
  }, [
    compareActive,
    compareCategorySync,
    verticalComparePaneCount,
    rootCategory.id,
    treeSubcasesLeft,
    verticalMiddleSubcases,
    verticalRightSubcases,
    syncCompareFromLeftPane,
  ]);

  const allMatchesUnified = useMemo(() => {
    const left = collectSearchMatches(
      items as TreeRowItem[],
      0,
      searchWords,
      wholeWordOnly,
      regex,
      caseSensitive
    );
    if (!compareActive) return left;
    if (verticalComparePaneCount === 3) {
      const mid = collectSearchMatches(
        itemsMiddle as TreeRowItem[],
        1,
        searchWords,
        wholeWordOnly,
        regex,
        caseSensitive
      );
      const right = collectSearchMatches(
        itemsRight as TreeRowItem[],
        2,
        searchWords,
        wholeWordOnly,
        regex,
        caseSensitive
      );
      return [...left, ...mid, ...right];
    }
    const right = collectSearchMatches(
      itemsRight as TreeRowItem[],
      1,
      searchWords,
      wholeWordOnly,
      regex,
      caseSensitive
    );
    return [...left, ...right];
  }, [
    items,
    itemsMiddle,
    itemsRight,
    compareActive,
    verticalComparePaneCount,
    searchWords,
    wholeWordOnly,
    regex,
    caseSensitive,
  ]);

  const compareSearchJumpTargets = useMemo(() => {
    if (!compareActive || allMatchesUnified.length === 0) {
      return { firstMatchIndexByPane: {} as Partial<Record<SearchMatchPane, number>> };
    }
    const firstMatchIndexByPane: Partial<Record<SearchMatchPane, number>> = {};
    for (let i = 0; i < allMatchesUnified.length; i++) {
      const p = allMatchesUnified[i].pane;
      if (firstMatchIndexByPane[p] === undefined) {
        firstMatchIndexByPane[p] = i;
      }
    }
    return { firstMatchIndexByPane };
  }, [compareActive, allMatchesUnified]);

  function getSelectedMatchForItem(pane: SearchMatchPane, itemIdx: number) {
    const global = allMatchesUnified[currentMatch];
    if (!global || global.pane !== pane || global.itemIdx !== itemIdx) return undefined;
    return { matchIdx: global.matchIdx, wordIdx: global.wordIdx };
  }

  const getItemHeight = (item: { content?: string } | undefined) => {
    if (!item?.content) return 50;
    const baseHeight = 40;
    const contentHeight = Math.ceil(item.content.length / 80) * 20;
    return Math.max(baseHeight, contentHeight);
  };

  // Render function for each item
  const renderItem = (
    item: (typeof items)[number],
    index: number,
    opts?: { pane?: SearchMatchPane; suppressSelectedMatch?: boolean }
  ) => {
    if (!item.content) return null;
    const pane: SearchMatchPane = opts?.pane ?? 0;
    const suppressSelectedMatch = opts?.suppressSelectedMatch ?? false;
    const paddingStyle = isRTL
      ? { paddingRight: item.level * 4 }
      : { paddingLeft: item.level * 4 };
    const selectedMatch = suppressSelectedMatch ? undefined : getSelectedMatchForItem(pane, index);
    switch (item.type) {
      case 'category':
      case 'contentCategory': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        // Find the category in allCategories to check if it's a content category
        const isContentCategory = item.type === 'contentCategory';

        // Use DescriptionIcon for content categories, FolderIcon for regular categories
        const CategoryIcon = isContentCategory ? DescriptionIcon : FolderIcon;
        const categoryColors = compareActive
          ? compareCategoryRowStyles(
              theme,
              comparePaneAccent(pane, verticalComparePaneCount),
              isContentCategory
            )
          : {
              iconColor: isContentCategory ? theme.palette.info.light : theme.palette.primary.main,
              textColor: isContentCategory ? theme.palette.info.light : theme.palette.primary.main,
            };
        const iconMargin = isRTL ? { ml: 1 } : { mr: 1 };

        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1,
              px: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: getItemHeight(item),
              ...paddingStyle,
            }}
          >
            <CategoryIcon sx={{ color: categoryColors.iconColor, fontSize: 20, flexShrink: 0, ...iconMargin }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                wordBreak: 'break-word',
                flex: 1,
                color: categoryColors.textColor,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      case 'subcase': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        const iconMargin = isRTL ? { ml: 1 } : { mr: 1 };
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: getItemHeight(item),
              ...paddingStyle,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
              <StickyNote2OutlinedIcon sx={{ color: 'warning.main', fontSize: 16, flexShrink: 0, ...iconMargin }} />
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  flex: 1,
                  color: 'text.primary',
                  lineHeight: 1.4,
                }}
              >
                {highlighted}
              </Typography>
            </Box>
          </Box>
        );
      }
      case 'content': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: getItemHeight(item),
              ...paddingStyle,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                flex: 1,
                color: 'text.primary',
                lineHeight: 1.4,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      case 'description': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              minHeight: getItemHeight(item),
              ...paddingStyle,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  // Scroll the pane that contains the current match (single or compare mode)
  React.useEffect(() => {
    if (!allMatchesUnified.length) return;
    const entry = allMatchesUnified[currentMatch];
    if (!entry) return;
    const t = window.setTimeout(() => {
      compareSyncLockRef.current = true;
      const vRef =
        entry.pane === 0
          ? virtuosoRef
          : entry.pane === 1 && verticalComparePaneCount === 3
            ? virtuosoRefMiddle
            : virtuosoRefRight;
      const itemList: TreeRowItem[] =
        entry.pane === 0
          ? (items as TreeRowItem[])
          : entry.pane === 1 && verticalComparePaneCount === 3
            ? (itemsMiddle as TreeRowItem[])
            : (itemsRight as TreeRowItem[]);
      vRef.current?.scrollToIndex({
        index: entry.itemIdx,
        align: 'start',
        behavior: 'auto',
      });
      const categoryId = categoryIdForRowIndex(itemList, entry.itemIdx);
      if (categoryId) {
        compareLastCategoryByPaneRef.current[entry.pane] = categoryId;
        const categoryItem = itemList.find((row) => categoryRowId(row) === categoryId);
        setSyncedCategoryLabel(categoryItem?.content?.trim() || null);
      }
      window.setTimeout(() => {
        compareSyncLockRef.current = false;
      }, 40);
    }, 50);
    return () => window.clearTimeout(t);
  }, [currentMatch, allMatchesUnified, verticalComparePaneCount, items, itemsMiddle, itemsRight]);

  React.useEffect(() => {
    setCurrentMatch((m) => {
      if (allMatchesUnified.length === 0) return 0;
      return Math.min(m, allMatchesUnified.length - 1);
    });
  }, [allMatchesUnified]);

  React.useEffect(() => {
    try {
      localStorage.setItem('subtree_horizontal_zoom', String(horizontalZoom));
    } catch {
      // ignore
    }
  }, [horizontalZoom]);

  const recenterHorizontalView = React.useCallback(() => {
    if (layoutMode !== 'horizontal') return;
    const scroller = horizontalScrollRef.current;
    const info = horizontalLayoutRef.current;
    if (!scroller || !info) return;

    const zx = info.rootX * horizontalZoom;
    const zy = info.rootY * horizontalZoom;

    const targetX = Math.max(0, zx - scroller.clientWidth / 2);
    const targetY = Math.max(0, zy - scroller.clientHeight / 2);

    const apply = () => {
      const beforeX = scroller.scrollLeft;
      scroller.scrollLeft = targetX;
      scroller.scrollTop = targetY;
      if (isRTL && scroller.scrollLeft === beforeX && scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth - targetX);
      }
    };

    // Let layout settle
    window.setTimeout(apply, 0);
  }, [horizontalZoom, isRTL, layoutMode]);

  React.useEffect(() => {
    if (layoutMode !== 'horizontal') return;
    const scroller = horizontalScrollRef.current;
    const info = horizontalLayoutRef.current;
    if (!scroller || !info) return;

    // center root horizontally in the scroll container
    const target = Math.max(0, info.rootX * horizontalZoom - scroller.clientWidth / 2);

    // Different browsers handle RTL scrollLeft differently. Try LTR-style first,
    // and if it doesn't move, try the mirrored value.
    const apply = () => {
      const before = scroller.scrollLeft;
      scroller.scrollLeft = target;
      if (isRTL && scroller.scrollLeft === before && scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth - target);
      }
    };

    // Let layout settle (fonts + DOM)
    const t = window.setTimeout(apply, 0);
    return () => window.clearTimeout(t);
  }, [
    layoutMode,
    rootCategory.id,
    isRTL,
    horizontalZoom,
    horizontalShowSubcaseList,
    subcases,
  ]);

  const activeCompareSearchPane: SearchMatchPane | null =
    compareActive && search.trim().length > 0 && allMatchesUnified.length > 0
      ? allMatchesUnified[currentMatch]?.pane ?? null
      : null;

  const rightPaneIndex: SearchMatchPane = verticalComparePaneCount === 3 ? 2 : 1;
  const leftColumnChrome = compareColumnShellSx(
    comparePaneAccent(0, verticalComparePaneCount),
    activeCompareSearchPane === 0
  );
  const middleColumnChrome = compareColumnShellSx(
    comparePaneAccent(1, verticalComparePaneCount),
    activeCompareSearchPane === 1
  );
  const rightColumnChrome = compareColumnShellSx(
    comparePaneAccent(rightPaneIndex, verticalComparePaneCount),
    activeCompareSearchPane === rightPaneIndex
  );

  return (
    <>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
      {(!layoutModeControlled || layoutMode === 'horizontal') && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: 2,
          pr: 6,
          flexWrap: 'wrap',
          gap: 1,
          flexShrink: 0,
        }}
      >
          {layoutMode === 'horizontal' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Export PNG (paste into Word/Google Docs)">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportHorizontalPng}
                    size="small"
                    disabled={exportingHorizontalPng}
                    sx={{ mr: 1 }}
                  >
                    {exportingHorizontalPng ? 'Exporting…' : 'Export PNG'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Recenter view">
                <span>
                  <IconButton size="small" onClick={recenterHorizontalView}>
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom out">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setHorizontalZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
                    disabled={horizontalZoom <= 0.5}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" sx={{ minWidth: 54, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(horizontalZoom * 100)}%
              </Typography>
              <Tooltip title="Zoom in">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setHorizontalZoom((z) => Math.min(2.0, Math.round((z + 0.1) * 10) / 10))}
                    disabled={horizontalZoom >= 2.0}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reset zoom">
                <IconButton size="small" onClick={() => setHorizontalZoom(1)}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <FormGroup row sx={{ ml: 1, gap: 0.5, alignItems: 'center' }}>
                <Tooltip title="Shade each content category by how many of the selected subcases have content there (darker = more coverage).">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={horizontalCoverageShading}
                        onChange={e => setHorizontalCoverageShading(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Coverage shading</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Tooltip>
                <Tooltip title="List selected subcases that have content under each content category.">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={horizontalShowSubcaseList}
                        onChange={e => setHorizontalShowSubcaseList(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Subcase list</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Tooltip>
              </FormGroup>
            </Box>
          )}
          {!layoutModeControlled && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'stretch',
              '& .MuiToggleButton-root:first-of-type': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
              '& .MuiToggleButton-root:last-of-type': {
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                marginLeft: '-1px',
              },
            }}
          >
            <Tooltip
              title={
                getToolbarTooltipTextForState('subtree-layout-vertical', true) ||
                helpContent['subtree-layout-vertical'].simple
              }
              placement="bottom"
            >
              <span>
                <ToggleButton
                  size="small"
                  value="vertical"
                  selected={layoutMode === 'vertical'}
                  onClick={() => applyLayoutMode('vertical')}
                >
                  Vertical
                </ToggleButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                getToolbarTooltipTextForState('subtree-layout-horizontal', true) ||
                helpContent['subtree-layout-horizontal'].simple
              }
              placement="bottom"
            >
              <span>
                <ToggleButton
                  size="small"
                  value="horizontal"
                  selected={layoutMode === 'horizontal'}
                  onClick={() => applyLayoutMode('horizontal')}
                >
                  Horizontal
                </ToggleButton>
              </span>
            </Tooltip>
          </Box>
          )}
      </Box>
      )}
      {layoutMode === 'vertical' && (
        <FormGroup row sx={{ gap: 1, alignItems: 'flex-start', justifyContent: 'space-between', mt: 0, mb: 0, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={getTooltipTextForState('subtree-search-category', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.category} onChange={e => setSearchFields(prev => ({ ...prev, category: e.target.checked }))} />}
              label="Category names"
            />
          </Tooltip>

          <Tooltip title={getTooltipTextForState('subtree-search-description', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.description} onChange={e => setSearchFields(prev => ({ ...prev, description: e.target.checked }))} />}
              label="Descriptions"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-search-subcase', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.subcase} onChange={e => setSearchFields(prev => ({ ...prev, subcase: e.target.checked }))} />}
              label="Subcases"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-search-content', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.content} onChange={e => setSearchFields(prev => ({ ...prev, content: e.target.checked }))} />}
              label="Content text"
            />
          </Tooltip>
          {sourceTexts && (
            <Tooltip title={getTooltipTextForState('subtree-show-sources', true)}>
              <FormControlLabel
                control={<Checkbox checked={showSourceTexts} onChange={e => setShowSourceTexts(e.target.checked)} />}
                label="Show source texts"
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportDocx}
            size="small"
          >
            Export DOCX
          </Button>
        </Box>
      </FormGroup>
      )}

      {layoutMode === 'vertical' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.75,
            mb: 1,
            mt: 0.5,
            flexShrink: 0,
          }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
        <Tooltip title={getTooltipTextForState('subtree-search-input', true)}>
          <TextField
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            onFocus={handleSearchFocus}
            placeholder='Search (multiple words or "phrases")'
            size='small'
            sx={{ minWidth: 320 }}
            InputProps={{
              endAdornment: (
                search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch('')}
                      aria-label="Clear search"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              )
            }}
          />
        </Tooltip>
        <Popper open={!!searchBoxAnchor && !search && searchHistory.length > 0} anchorEl={searchBoxAnchor} placement="bottom-start" style={{ zIndex: 1301 }}>
          <Paper sx={{ width: 320 }}>
            <List dense>
              {searchHistory.map((item, idx) => (
                <ListItemButton key={item + idx} onMouseDown={() => handleHistoryClick(item)}>
                  <ListItemText primary={item} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
        <FormGroup row sx={{ gap: 1 }}>
          <Tooltip title={getTooltipTextForState('subtree-case-sensitive', true)}>
            <FormControlLabel
              control={<Checkbox checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} />}
              label="Case sensitive"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-whole-word', true)}>
            <FormControlLabel
              control={<Checkbox checked={wholeWordOnly} onChange={e => setWholeWordOnly(e.target.checked)} />}
              label="Whole word only"
            />
          </Tooltip>
          <Tooltip title="Show multiple vertical lists side by side, each with its own subcase filter (same search applies to all). Choose 2 or 3 views when enabled.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={verticalCompareSplit}
                  onChange={(e) => {
                    const on = e.target.checked;
                    if (on) {
                      setVerticalLeftSubcases([...subcases]);
                      setVerticalRightSubcases([...subcases]);
                      setVerticalMiddleSubcases(verticalComparePaneCount === 3 ? [...subcases] : []);
                      setVerticalLeftGroupNames([]);
                      setVerticalMiddleGroupNames([]);
                      setVerticalRightGroupNames([]);
                      verticalCompareLeftBaselineRef.current = null;
                      verticalCompareMiddleBaselineRef.current = null;
                      verticalCompareRightBaselineRef.current = null;
                      setCurrentMatch(0);
                    }
                    setVerticalCompareSplit(on);
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SplitscreenIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Compare filters</Typography>
                </Box>
              }
            />
          </Tooltip>
        </FormGroup>
        </Box>
        {verticalCompareSplit && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
            <ToggleButtonGroup
              size="small"
              value={verticalComparePaneCount}
              exclusive
              onChange={(_, v) => {
                if (v == null) return;
                if (v === 2) {
                  setVerticalComparePaneCount(2);
                  setVerticalMiddleSubcases([]);
                  setVerticalMiddleGroupNames([]);
                  verticalCompareMiddleBaselineRef.current = null;
                  setCompareFilterOpen((prev) => (prev?.which === 'middle' ? null : prev));
                } else {
                  setVerticalComparePaneCount(3);
                  setVerticalMiddleSubcases((prev) => (prev.length > 0 ? prev : [...subcases]));
                }
                setCurrentMatch(0);
              }}
            >
              <ToggleButton value={2}>2 views</ToggleButton>
              <ToggleButton value={3}>3 views</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Align all columns to the category visible in the left column. Scrolling the left column updates the others when you reach a new category; scrolling within the same category does not move the other columns.">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={compareCategorySync}
                    onChange={(e) => setCompareCategorySync(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SyncAltIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">Sync categories</Typography>
                  </Box>
                }
              />
            </Tooltip>
            {compareCategorySync && compareContentCategoryNav.hasAny && (
              <>
                <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch', my: 0.25 }} />
                <Tooltip title="Previous content category">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => jumpCompareContentCategory(-1)}
                      disabled={!compareContentCategoryNav.canPrev}
                      aria-label="Previous content category"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Next content category">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => jumpCompareContentCategory(1)}
                      disabled={!compareContentCategoryNav.canNext}
                      aria-label="Next content category"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {compareCategorySync && syncedCategoryLabel && (
              <>
                <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch', my: 0.25 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    wordBreak: 'break-word',
                    maxWidth: 480,
                  }}
                >
                  {syncedCategoryLabel}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
      )}

      {layoutMode === 'vertical' && search && allMatchesUnified.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
            {currentMatch + 1} / {allMatchesUnified.length}
          </Typography>
          {compareActive && allMatchesUnified[currentMatch] && (() => {
            const p = allMatchesUnified[currentMatch].pane;
            const capColor =
              p === 0
                ? 'info.main'
                : p === 1 && verticalComparePaneCount === 3
                  ? 'error.main'
                  : 'secondary.main';
            return (
              <Typography variant="caption" sx={{ fontWeight: 600, color: capColor }}>
                ({comparePaneTitle(p, verticalComparePaneCount)} pane)
              </Typography>
            );
          })()}
          {compareActive && (
            <>
              <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch', my: 0.5 }} />
              <Tooltip title="Jump to the first search hit in the left column">
                <span>
                  <Button
                    size="small"
                    variant={activeCompareSearchPane === 0 ? 'contained' : 'outlined'}
                    color="info"
                    disabled={compareSearchJumpTargets.firstMatchIndexByPane[0] === undefined}
                    onClick={() =>
                      setCurrentMatch(compareSearchJumpTargets.firstMatchIndexByPane[0] ?? 0)
                    }
                    sx={{ minWidth: 52, textTransform: 'none', py: 0.25 }}
                  >
                    Left
                  </Button>
                </span>
              </Tooltip>
              {verticalComparePaneCount === 3 && (
                <Tooltip title="Jump to the first search hit in the middle column">
                  <span>
                    <Button
                      size="small"
                      variant={activeCompareSearchPane === 1 ? 'contained' : 'outlined'}
                      color="error"
                      disabled={compareSearchJumpTargets.firstMatchIndexByPane[1] === undefined}
                      onClick={() =>
                        setCurrentMatch(compareSearchJumpTargets.firstMatchIndexByPane[1] ?? 0)
                      }
                      sx={{ minWidth: 58, textTransform: 'none', py: 0.25 }}
                    >
                      Middle
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Jump to the first search hit in the right column">
                <span>
                  <Button
                    size="small"
                    variant={activeCompareSearchPane === rightPaneIndex ? 'contained' : 'outlined'}
                    color="secondary"
                    disabled={
                      compareSearchJumpTargets.firstMatchIndexByPane[rightPaneIndex] === undefined
                    }
                    onClick={() =>
                      setCurrentMatch(
                        compareSearchJumpTargets.firstMatchIndexByPane[rightPaneIndex] ?? 0
                      )
                    }
                    sx={{ minWidth: 52, textTransform: 'none', py: 0.25 }}
                  >
                    Right
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title={getTooltipTextForState('subtree-previous-match', true)}>
            <IconButton
              size="small"
              onClick={() =>
                setCurrentMatch((prev) => (prev - 1 + allMatchesUnified.length) % allMatchesUnified.length)
              }
              disabled={allMatchesUnified.length === 0}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-next-match', true)}>
            <IconButton
              size="small"
              onClick={() => setCurrentMatch((prev) => (prev + 1) % allMatchesUnified.length)}
              disabled={allMatchesUnified.length === 0}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      {layoutMode === 'vertical' ? (
        compareActive ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                width: '100%',
                flex: 1,
                minHeight: 0,
              }}
            >
            {/* Left column */}
            <Box sx={leftColumnChrome.column}>
              <Box sx={leftColumnChrome.header}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: leftColumnChrome.labelColor,
                  }}
                >
                  Left
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color={leftColumnChrome.buttonColor}
                  onClick={(e) => setCompareFilterOpen({ which: 'left', anchor: e.currentTarget })}
                >
                  Subcases ({verticalLeftSubcases.length})
                </Button>
              </Box>
              <Virtuoso
                ref={virtuosoRef}
                style={{ flex: 1, minHeight: 0, height: '100%', width: '100%' }}
                totalCount={items.length}
                itemContent={(index: number) => renderItem(items[index], index, { pane: 0 })}
                computeItemKey={(index) => items[index].id}
                rangeChanged={(range) => handleComparePaneRangeChanged(0, range.startIndex)}
                components={{ Scroller: compareVirtuosoScroller }}
              />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
            {verticalComparePaneCount === 3 && (
              <>
                {/* Middle column */}
                <Box sx={middleColumnChrome.column}>
                  <Box sx={middleColumnChrome.header}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: middleColumnChrome.labelColor,
                      }}
                    >
                      Middle
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color={middleColumnChrome.buttonColor}
                      onClick={(e) => setCompareFilterOpen({ which: 'middle', anchor: e.currentTarget })}
                    >
                      Subcases ({verticalMiddleSubcases.length})
                    </Button>
                  </Box>
                  <Virtuoso
                    ref={virtuosoRefMiddle}
                    style={{ flex: 1, minHeight: 0, height: '100%', width: '100%' }}
                    totalCount={itemsMiddle.length}
                    itemContent={(index: number) =>
                      itemsMiddle[index]
                        ? renderItem(itemsMiddle[index], index, { pane: 1 })
                        : null
                    }
                    computeItemKey={(index) => itemsMiddle[index]?.id ?? `mid-${index}`}
                    rangeChanged={(range) => handleComparePaneRangeChanged(1, range.startIndex)}
                    components={{ Scroller: compareVirtuosoScroller }}
                  />
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
              </>
            )}
            {/* Right column */}
            <Box sx={rightColumnChrome.column}>
              <Box sx={rightColumnChrome.header}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: rightColumnChrome.labelColor,
                  }}
                >
                  Right
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color={rightColumnChrome.buttonColor}
                  onClick={(e) => setCompareFilterOpen({ which: 'right', anchor: e.currentTarget })}
                >
                  Subcases ({verticalRightSubcases.length})
                </Button>
              </Box>
              <Virtuoso
                ref={virtuosoRefRight}
                style={{ flex: 1, minHeight: 0, height: '100%', width: '100%' }}
                totalCount={itemsRight.length}
                itemContent={(index: number) =>
                  itemsRight[index]
                    ? renderItem(itemsRight[index], index, { pane: rightPaneIndex })
                    : null
                }
                computeItemKey={(index) => itemsRight[index]?.id ?? `empty-${index}`}
                rangeChanged={(range) => handleComparePaneRangeChanged(rightPaneIndex, range.startIndex)}
                components={{ Scroller: compareVirtuosoScroller }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
            <Virtuoso
              ref={virtuosoRef}
              style={{ height: '100%', width: '100%' }}
              totalCount={items.length}
              itemContent={(index: number) => renderItem(items[index], index)}
              computeItemKey={(index) => items[index].id}
              components={{ Scroller: compareVirtuosoScroller }}
            />
          </Box>
        )
      ) : (
        <Box
          ref={horizontalScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            p: 1,
            cursor: 'grab',
            userSelect: 'none',
          }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            const scroller = horizontalScrollRef.current;
            if (!scroller) return;

            // Don't start panning when interacting with a button/link/control inside.
            const target = e.target as HTMLElement | null;
            if (target && target.closest('button, [role="button"], a, input, textarea, select, [contenteditable="true"]')) {
              return;
            }

            horizontalDragRef.current = {
              isDragging: true,
              startX: e.clientX,
              startY: e.clientY,
              startScrollLeft: scroller.scrollLeft,
              startScrollTop: scroller.scrollTop,
            };
            try {
              (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            } catch {
              // ignore
            }
            scroller.style.cursor = 'grabbing';
          }}
          onPointerMove={(e) => {
            const scroller = horizontalScrollRef.current;
            if (!scroller) return;
            const state = horizontalDragRef.current;
            if (!state.isDragging) return;
            e.preventDefault();
            const dx = e.clientX - state.startX;
            const dy = e.clientY - state.startY;
            scroller.scrollLeft = state.startScrollLeft - dx;
            scroller.scrollTop = state.startScrollTop - dy;
          }}
          onPointerUp={() => {
            const scroller = horizontalScrollRef.current;
            horizontalDragRef.current.isDragging = false;
            if (scroller) scroller.style.cursor = 'grab';
          }}
          onPointerCancel={() => {
            const scroller = horizontalScrollRef.current;
            horizontalDragRef.current.isDragging = false;
            if (scroller) scroller.style.cursor = 'grab';
          }}
          onPointerLeave={() => {
            const scroller = horizontalScrollRef.current;
            horizontalDragRef.current.isDragging = false;
            if (scroller) scroller.style.cursor = 'grab';
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Horizontal view shows the category tree. Use &quot;Coverage shading&quot; to compare selected subcases across
            content categories, and &quot;Subcase list&quot; to see which selected subcases appear under each category.
          </Typography>
          <Box
            ref={horizontalExportRef}
            sx={{
              width: 'max-content',
              height: 'max-content',
              bgcolor: exportingHorizontalPng ? '#ffffff' : 'transparent',
            }}
          >
            <HorizontalSubTreeGraph
              rootCategory={rootCategory}
              isRTL={isRTL}
              scale={horizontalZoom}
              selectedSubcases={subcases}
              coverageShading={horizontalCoverageShading}
              showSubcaseList={horizontalShowSubcaseList}
              forPngExport={exportingHorizontalPng}
              onLayout={(info) => {
                horizontalLayoutRef.current = info;
              }}
            />
          </Box>
        </Box>
      )}
      </Box>
      </Box>
      <Popover
        open={compareFilterOpen !== null}
        anchorEl={compareFilterOpen?.anchor ?? null}
        onClose={() => setCompareFilterOpen(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper sx={{ p: 1.5, width: 320, maxHeight: 420, overflow: 'auto' }}>
          {docSubcasesForFilter.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No subcases available for this document.
            </Typography>
          ) : (
            compareFilterOpen &&
            (() => {
              const which = compareFilterOpen.which;
              const groupNames =
                which === 'left'
                  ? verticalLeftGroupNames
                  : which === 'middle'
                    ? verticalMiddleGroupNames
                    : verticalRightGroupNames;
              const paneSubcases =
                which === 'left'
                  ? verticalLeftSubcases
                  : which === 'middle'
                    ? verticalMiddleSubcases
                    : verticalRightSubcases;
              const baselineRef =
                which === 'left'
                  ? verticalCompareLeftBaselineRef
                  : which === 'middle'
                    ? verticalCompareMiddleBaselineRef
                    : verticalCompareRightBaselineRef;
              const setPaneSubcases =
                which === 'left'
                  ? setVerticalLeftSubcases
                  : which === 'middle'
                    ? setVerticalMiddleSubcases
                    : setVerticalRightSubcases;
              const setPaneGroupNames =
                which === 'left'
                  ? setVerticalLeftGroupNames
                  : which === 'middle'
                    ? setVerticalMiddleGroupNames
                    : setVerticalRightGroupNames;

              return (
                <>
                  {subcaseGroupsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading groups…</Typography>
                    </Box>
                  ) : (
                    subcaseGroups.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          Groups
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {subcaseGroups.map((group) => (
                            <Chip
                              key={`cf-${which}-gr-${group.name}`}
                              label={group.name}
                              size="small"
                              color={groupNames.includes(group.name) ? 'primary' : 'default'}
                              onClick={() => handleCompareGroupSelect(which, group.name)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Subcases
                  </Typography>
                  <FormGroup>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              paneSubcases.length === docSubcasesForFilter.length &&
                              docSubcasesForFilter.length > 0
                            }
                            indeterminate={
                              paneSubcases.length > 0 &&
                              paneSubcases.length < docSubcasesForFilter.length
                            }
                            onChange={(e) => {
                              baselineRef.current = null;
                              setPaneGroupNames([]);
                              if (e.target.checked) {
                                setPaneSubcases([...docSubcasesForFilter]);
                              } else {
                                setPaneSubcases([]);
                              }
                            }}
                          />
                        }
                        label={<Typography variant="body2">Select all</Typography>}
                      />
                      <InvertSelectionButton
                        disabled={docSubcasesForFilter.length === 0}
                        onClick={() => {
                          baselineRef.current = null;
                          setPaneGroupNames([]);
                          setPaneSubcases((prev) =>
                            invertSubcaseSelection(docSubcasesForFilter, prev),
                          );
                        }}
                      />
                    </Box>
                    {docSubcasesForFilter.map((sc) => (
                      <FormControlLabel
                        key={`cf-${which}-${sc}`}
                        control={
                          <Checkbox
                            size="small"
                            checked={paneSubcases.includes(sc)}
                            onChange={() => {
                              setPaneSubcases((prev) =>
                                prev.includes(sc) ? prev.filter((x) => x !== sc) : [...prev, sc]
                              );
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {sc}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </>
              );
            })()
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default SubTreeView;